import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Icon } from "@iconify-icon/react";
import { useStore } from "../../../stores/StoreContext";

import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import MessageTile from "../../../HelperComponents/MessageTile";
import DetailedList from "../../../HelperComponents/DetailedList";
import { Document } from ".././ViewDocs/Documents.types";
import { getSimpleDateFormat } from "../../../Utility/Utility";
import UploadDocument from ".././ViewDocs/UploadDocument";
import DocumentDetailPanel from "./DocumentDetailPanel";
import RecycleBinPanel from "./RecycleBinPanel";
import RegionTransferBanner from "./RegionTransferBanner";

/**
 * ViewDocuments Component
 * Displays a grouped list of documents with action buttons
 * Uses MobX stores for state management and UserContext for user data
 */
const ViewDocuments: React.FC = observer(() => {
  const { documentStore, uiStore, userStore } = useStore();
  const user = userStore.user;
  const isUserLoading = userStore.isLoading;

  // Fetch documents on mount
  useEffect(() => {
    // Wait for user data to finish loading before attempting to fetch documents
    if (isUserLoading) {
      return;
    }

    if (user?.LoginName) {
      documentStore.fetchDocumentsForCurrentUser();
      // Approved future-dated transfer drives the Current/New region toggle
      documentStore.fetchRegionalTransfer();
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.LoginName, isUserLoading]);

  // Clear messages when component unmounts
  useEffect(() => {
    return () => {
      uiStore.clearError();
      uiStore.clearSuccessMessage();
      // Start the next visit in the loading state until its transfer check finishes
      documentStore.resetRegionalTransfer();
    };
  }, [uiStore, documentStore]);

  // Action handlers - documents are read-only for users (view metadata / download only)
  const handleView = useCallback(
    (document: Document) => {
      documentStore.selectDocument(document);
      uiStore.openViewPanel();
    },
    [documentStore, uiStore],
  );

  // Column definitions (matching legacy ViewDocuments.tsx columns)
  const columns = useMemo(
    () => [
      {
        key: "FileName",
        name: "Title",
        fieldName: "FileName",
        minWidth: 250,
        maxWidth: 300,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => {
          if (!item.FileName) return <span>Unknown</span>;

          // Render as Link if URL exists (matches legacy behavior - line 79-88)
          if (item.Url) {
            return (
              <a
                href={item.Url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  color: "#31708f",
                }}
              >
                {item.FileName}
              </a>
            );
          }

          // Fallback to plain text if no URL
          return <span>{item.FileName}</span>;
        },
      },
      {
        key: "CreatedBy",
        name: "Created By",
        fieldName: "CreatedBy",
        minWidth: 130,
        maxWidth: 150,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => (
          <span>{item.CreatedBy?.replace("positive\\", "") || ""}</span>
        ),
      },
      {
        key: "Created",
        name: "Created On",
        fieldName: "Created",
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => {
          if (!item.Created) return <span></span>;
          return <span>{getSimpleDateFormat(item.Created)}</span>;
        },
      },
      {
        key: "ModifiedBy",
        name: "Modified By",
        fieldName: "ModifiedBy",
        minWidth: 130,
        maxWidth: 150,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => (
          <span>{item.ModifiedBy?.replace("positive\\", "") || ""}</span>
        ),
      },
      {
        key: "Modified",
        name: "Modified On",
        fieldName: "Modified",
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => {
          if (!item.Modified) return <span></span>;
          return <span>{getSimpleDateFormat(item.Modified)}</span>;
        },
      },
      {
        key: "IssuedDate",
        name: "Issued Date",
        fieldName: "IssuedDate",
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => {
          if (!item.IssuedDate) return <span></span>;
          return <span>{getSimpleDateFormat(item.IssuedDate)}</span>;
        },
      },
      {
        key: "Expires",
        name: "Expires",
        fieldName: "Expires",
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSortable: true,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => {
          if (!item.Expires) return <span></span>;
          return <span>{getSimpleDateFormat(item.Expires)}</span>;
        },
      },
      {
        key: "Actions",
        name: "",
        fieldName: "Actions",
        minWidth: 100,
        maxWidth: 100,
        isResizable: false,
        isSortable: false,
        headerClassName: "detailedListHeader",
        className: "detailedListColumn",
        onRender: (item: Document) => (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => handleView(item)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 4,
                borderRadius: 9999,
                color: "hsl(var(--primary))",
              }}
              title="View Document"
            >
              <Icon icon="mdi:eye" width="20" height="20" />
            </button>
          </div>
        ),
      },
    ],
    [handleView],
  );

  // Group documents by ContentType (structure expected by DetailedList for grouped mode)
  const groups = useMemo(() => {
    const grouped = documentStore.groupedDocuments;
    const groupKeys = Object.keys(grouped).sort(); // Sort alphabetically for consistent order

    let startIndex = 0;
    return groupKeys.map((contentType) => {
      const groupItems = grouped[contentType];
      const group = {
        key: contentType,
        name: contentType,
        startIndex,
        count: groupItems.length,
        level: 0,
        isCollapsed: false,
      };
      startIndex += groupItems.length;
      return group;
    });
  }, [documentStore.groupedDocuments]);

  // Flatten grouped documents for DetailsList - MUST match the order of groups
  const items: Document[] = useMemo(() => {
    const grouped = documentStore.groupedDocuments;
    const groupKeys = Object.keys(grouped).sort(); // Sort alphabetically to match groups order
    return groupKeys.flatMap((key) => grouped[key]);
  }, [documentStore.groupedDocuments]);

  // Styles (mapped into DetailedList styles.root)
  const listStyles = {
    root: {
      margin: "10px 0",
      width: "100%",
    } as React.CSSProperties,
  };

  // Loading state - show nothing until the user, documents and regional transfer
  // check have all settled, so the banner and Upload Document appear with the list
  const isPageLoading =
    isUserLoading ||
    documentStore.isLoading ||
    (!!user?.LoginName && documentStore.isRegionalTransferLoading);

  if (isPageLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ padding: "16px" }}>
      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        {/* <button
          type="button"
          onClick={handleManualRefresh}
          disabled={documentStore.isLoading}
          style={{
            padding: "8px 16px",
            borderRadius: 9999,
            border: "1px solid var(--app-border-color)",
            backgroundColor: "hsl(var(--secondary))",
            color: "hsl(var(--secondary-foreground))",
            cursor: documentStore.isLoading ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
            opacity: documentStore.isLoading ? 0.7 : 1,
          }}
          title="Refresh document list"
        >
          <Icon
            icon={documentStore.isLoading ? "mdi:loading" : "mdi:refresh"}
            width="20"
            height="20"
            className={documentStore.isLoading ? "animate-spin" : ""}
          />
          {documentStore.isLoading ? "Refreshing..." : "Refresh"}
        </button> */}

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={() => uiStore.openRecycleBin()}
            style={{
              padding: "8px 16px",
              borderRadius: 9999,
              border: "1px solid var(--app-border-color)",
              backgroundColor: "hsl(var(--secondary))",
              color: "hsl(var(--secondary-foreground))",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icon icon="mdi:trash-can-outline" width="20" height="20" />
            Recycle Bin
          </button>

          <button
            type="button"
            onClick={() => uiStore.openUploadDialog()}
            style={{
              padding: "8px 16px",
              borderRadius: 9999,
              border: "none",
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icon icon="mdi:upload" width="20" height="20" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Regional Transfer Toggle (only when an approved future transfer exists) */}
      <RegionTransferBanner />

      {/* Error Message */}
      {uiStore.error && (
        <MessageTile isVisible={true} message={uiStore.error} type="error" />
      )}

      {uiStore.successMessage && (
        <MessageTile
          isVisible={true}
          message={uiStore.successMessage}
          type="success"
        />
      )}

      {/* Document List */}
      {items.length > 0 ? (
        <DetailedList
          items={items}
          columns={columns}
          groups={groups}
          listType="grouped"
          styles={{ root: listStyles.root }}
        />
      ) : (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>No documents found.</p>
        </div>
      )}

      {/* Upload Document Dialog */}
      <UploadDocument />

      {/* Document Detail Panel */}
      <DocumentDetailPanel />

      {/* Recycle Bin Panel */}
      <RecycleBinPanel />
    </div>
  );
});

export default ViewDocuments;
